import { useEffect } from 'react';

/**
 * Runs an async effect that is cancelled on unmount or when `deps` change.
 *
 * `supabase-js` does not expose an AbortSignal we can wire into the network
 * layer, so cancellation here means "skip subsequent state updates" rather
 * than "stop the in-flight request". Pass `isCancelled()` to your fetcher and
 * early-return before calling setState if it returns true. Prevents the
 * "setState on unmounted component" warning and stops a stale fetch's result
 * from overwriting fresh data after a rapid dep change (e.g., tab switch).
 *
 * Usage:
 *   useAbortableFetch(({ isCancelled }) => {
 *     (async () => {
 *       const data = await fetcher();
 *       if (isCancelled()) return;
 *       setData(data);
 *     })();
 *   }, [user?.id]);
 */
export function useAbortableFetch(callback, deps = []) {
  useEffect(() => {
    let cancelled = false;
    const ctx = {
      isCancelled: () => cancelled,
    };
    callback(ctx);
    return () => {
      cancelled = true;
    };
    // Deps are explicitly forwarded from the caller; intentionally not
    // including `callback` so the caller doesn't have to memoize it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
